import { openDB } from 'idb';
import { UPLOAD_STATUSES } from 'constants/uploadStatuses';

const DB_NAME = 'upload-db';
const STORE_NAME = 'files';

let dbPromise = null;

export async function initDB() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          // If we change the keypath we need to destroy
          // and recreate the database
          db.createObjectStore(STORE_NAME, { keyPath: 'compositeId' });
        }
      }
    }).catch(error => {
      console.error('Failed to initialize IndexedDB:', error);
      throw error;
    });
  }
  return dbPromise;
}

// New function to put records in IndexedDB with proper error handling
function putRecord(db, storeName, record) {
  return new Promise((resolve, reject) => {
    try {
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(record);
      let result;

      // Resolve on commit, not on the put request succeeding. The request
      // reports success as soon as it is accepted, so resolving there swallows
      // a commit-time abort (DataError: Failed to write blobs) and reports a
      // write that never reached the disk.
      request.onsuccess = () => {
        result = request.result;
      };

      transaction.oncomplete = () => {
        resolve(result);
      };

      request.onerror = () => {
        console.error('Request error:', request.error);
        reject(request.error);
      };

      transaction.onerror = () => {
        console.error('Transaction error in IndexedDB:', transaction.error);
        reject(new Error('Database transaction failed.'));
      };

      transaction.onabort = () => {
        console.error('Transaction aborted in IndexedDB:', transaction.error);
        reject(new Error('Database transaction was aborted.'));
      };
    } catch (error) {
      console.error('Error in putRecord:', error);
      reject(error);
    }
  });
}

async function checkStorageAndCleanUp() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimation = await navigator.storage.estimate();
    const usage = estimation.usage || 0;
    const quota = estimation.quota || 0;
    const remaining = quota - usage;
    const remainingInMB = remaining / (1024 * 1024);
    const usedPercentage = (usage / quota) * 100;

    if (remainingInMB < 100) {
      console.error(
        'Computer may be too full, we may not be able to store temporary file uploads'
      );
    }
    // If storage is more than 90% full, attempt to delete old records
    if (usedPercentage > 90) {
      console.warn('IndexedDB usage is high. Cleaning up old records.');
      await deleteOldestAssessment();
    }
  }
}

async function deleteOldestAssessment() {
  try {
    const db = await initDB();
    const allRecords = await db.getAll(STORE_NAME);

    if (allRecords.length === 0) {
      console.warn('No records found in IndexedDB, could not delete.');
      return;
    }

    // Sort records by timestamp (oldest first)
    allRecords.sort((a, b) => a.timestamp - b.timestamp);

    // Delete the oldest record
    const oldestRecord = allRecords[0];
    await db.delete(STORE_NAME, oldestRecord.compositeId);
  } catch (error) {
    console.error('Error deleting oldest assessment:', error);
  }
}

export const generateFileCompositeId = (atttemptUUID, slotId) => {
  return `${atttemptUUID}-${slotId}`;
};

// Builds the record we put in the IndexedDB. Exported so callers can hold the
// same record in memory while the (async) write is still in flight.
export const buildFileRecord = (
  blob,
  attemptUUID,
  slotId,
  requestURL,
  assessmentSlug,
  questionNumber
) => ({
  compositeId: generateFileCompositeId(attemptUUID, slotId),
  blob,
  requestURL,
  slotId,
  status: UPLOAD_STATUSES.PENDING,
  assessmentSlug,
  questionNumber,
  timestamp: Date.now()
});

// blob is the file to upload blob
// compositeId is an id for the object made out of the attemptUUID and slotId
// slotId is the id of the slot or question
// requestURL is the URL to upload the video to
export async function storeFile(
  blob,
  attemptUUID,
  slotId,
  requestURL,
  assessmentSlug,
  questionNumber,
  dispatch
) {
  const fileRecord = buildFileRecord(
    blob,
    attemptUUID,
    slotId,
    requestURL,
    assessmentSlug,
    questionNumber
  );

  try {
    // Check storage before storing
    await checkStorageAndCleanUp();

    // Use native IndexedDB directly instead of the idb library
    const dbVersion = 1;

    const db = await new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = event => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'compositeId' });
        }
      };
    });

    // Use the native IndexedDB method with proper transaction handling
    await putRecord(db, STORE_NAME, fileRecord);
    dispatch({ type: 'IDB_UPDATED' });
    return fileRecord;
  } catch (error) {
    let sizeInMB = 0;
    if (blob && blob.size) {
      sizeInMB = (blob.size / (1024 * 1024)).toFixed(2);
    }
    console.error(
      `Error storing file record to IDB for attempt ${attemptUUID}, file size ${sizeInMB} Mb, question number ${questionNumber}:`,
      error
    );
    return fileRecord;
  }
}
export async function fileExists(attemptUUID, slotId) {
  try {
    const db = await initDB();
    const compositeId = generateFileCompositeId(attemptUUID, slotId);
    const record = await db.get(STORE_NAME, compositeId);
    return !!record;
  } catch (error) {
    console.error('Error checking file existence in IDB:', error);
    return false;
  }
}
export async function updateFileStatus(compositeId, status, error, dispatch) {
  try {
    const db = await initDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const record = await store.get(compositeId);
    if (record) {
      record.status = status;
      if (error) {
        record.error = error;
      }
      await store.put(record);
      if (dispatch) {
        dispatch({ type: 'IDB_UPDATED' });
      } else {
        console.error('No dispatch function provided to updateFileStatus');
      }
    } else {
      throw new Error(
        'IndexedDB record not found for compositeId:',
        compositeId
      );
    }
    await tx.done;
  } catch (error) {
    console.error('Error updating file record to IDB:', error);
    throw error;
  }
}
export async function getStoredFile(attemptUUID, slotId) {
  try {
    const db = await initDB();
    const compositeId = generateFileCompositeId(attemptUUID, slotId);
    const record = await db.get(STORE_NAME, compositeId); // Retrieve with compositeId
    return record || null;
  } catch (error) {
    console.error(
      `Error getting IDB file record for attempt: ${attemptUUID} and slot: ${slotId}`,
      error
    );
    return null;
  }
}
export async function getEveryUpload(attemptUUID = '') {
  try {
    const db = await initDB();
    const allRecords = await db.getAll(STORE_NAME);
    if (attemptUUID) {
      return allRecords.filter(record =>
        record.compositeId.startsWith(attemptUUID)
      );
    }
    return allRecords;
  } catch (error) {
    console.error('Error getting every IDB upload:', error);
    return null;
  }
}
export async function removeFile(attemptUUID, slotId, dispatch) {
  try {
    const db = await initDB();
    const compositeId = generateFileCompositeId(attemptUUID, slotId);
    // Delete with compositeId
    await db.delete(STORE_NAME, compositeId);
    dispatch({ type: 'IDB_UPDATED' });
  } catch (error) {
    console.error('Error removing IDB file record:', error);
  }
}
